import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import PhoneIcon from "@mui/icons-material/Phone";
import SearchIcon from "@mui/icons-material/Search";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import {
  Autocomplete,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { useCallback, useState } from "react";
import { useDebounce } from "use-debounce";
import {
  formatDateWithAge,
  formatPhoneNumber,
} from "../../common/components/CommonFunctions";
import { useInfiniteScroll } from "../../common/hooks/useInfiniteScroll";
import { usePatientControllerServiceGetPatientDropDown } from "../../services/queries";
import { AVATAR_COLOR, getStatusColor } from "./widget/constants";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store/store";

type PatientSearchProps = {
  selectedProviderUuids?: string[];
};

// Dropdown option row that shows the patient's full name in a tooltip while the row is hovered,
// but only when the name is cut off with an ellipsis. Overflow is measured on hover, so it stays
// correct however wide the dropdown renders.
const PatientOptionWithTooltip = ({
  name,
  children,
  ...liProps
}: React.HTMLAttributes<HTMLLIElement> & { name: string }) => {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  return (
    <Tooltip title={name} open={isTooltipOpen} placement="top-start">
      <li
        {...liProps}
        onMouseEnter={(e) => {
          liProps.onMouseEnter?.(e);
          const nameEl = e.currentTarget.querySelector<HTMLElement>(
            "[data-patient-name]",
          );
          setIsTooltipOpen(!!nameEl && nameEl.scrollWidth > nameEl.clientWidth);
        }}
        onMouseLeave={(e) => {
          liProps.onMouseLeave?.(e);
          setIsTooltipOpen(false);
        }}
      >
        {children}
      </li>
    </Tooltip>
  );
};

const PatientSearch = ({ selectedProviderUuids = [] }: PatientSearchProps) => {
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch] = useDebounce(searchText, 300);
  const hasSearchText = searchText.trim().length > 0;
  const { user, currentLocation } = useSelector(
    (state: RootState) => state?.auth,
  );
  const navbarSelectedProviders = useSelector(
    (state: RootState) => state.navbarProvider.selectedProviders,
  );
  const isDebouncing = searchText !== debouncedSearch;
  const isProviderUser = user?.roleType === "PROVIDER";
  const isClinicUser = user?.roleType === "CLINIC_USER";

  const providerUuids: string[] = (() => {
    const selected = navbarSelectedProviders
      .map((p) => p.uuid)
      .filter(Boolean) as string[];

    if (isProviderUser) {
      const own = user?.uuid ? [user.uuid] : [];
      return selected.length > 0 ? [...new Set([...own, ...selected])] : own;
    }
    if (isClinicUser) {
      return selected.length > 0 ? selected : selectedProviderUuids;
    }
    return selected;
  })();

  const locationUuids: string[] =
    isClinicUser && providerUuids.length === 0 && currentLocation
      ? [currentLocation]
      : [];

  const {
    options: patientOptions,
    handleScroll: handlePatientScroll,
    isFetching,
  } = useInfiniteScroll(
    (page: number) =>
      usePatientControllerServiceGetPatientDropDown({
        page,
        searchString: hasSearchText ? debouncedSearch || undefined : undefined,
        providerUuids,
        locationUuids,
      }),
    [debouncedSearch, hasSearchText, navbarSelectedProviders],
    0,
    "patientUuid",
  );

  const handlePatientSelect = useCallback((_event: any, patient: any) => {
    if (patient?.patientUuid) {
      window.open(
        `/ehr/patients/${patient.patientUuid}/charting-dashboard/facesheet`,
        "_blank",
      );
      setSearchText("");
    }
  }, []);

  return (
    <Autocomplete
      size="small"
      options={patientOptions || []}
      open={hasSearchText}
      getOptionLabel={(option: any) =>
        `${option.firstName || ""} ${option.lastName || ""}`.trim()
      }
      filterOptions={(x) => x}
      value={null}
      onChange={handlePatientSelect}
      inputValue={searchText}
      onInputChange={(_e, value, reason) => {
        if (reason !== "reset") {
          setSearchText(value);
        }
      }}
      ListboxProps={{
        onScroll: (event: React.UIEvent<HTMLUListElement>) => {
          const list = event.currentTarget;
          if (list.scrollTop + list.clientHeight >= list.scrollHeight - 10) {
            handlePatientScroll?.();
          }
        },
        style: { maxHeight: 400 },
      }}
      slotProps={{
        popper: {
          sx: { width: "280px !important" },
          placement: "bottom-end",
        },
      }}
      loading={isFetching || isDebouncing}
      noOptionsText={
        isFetching || isDebouncing
          ? "Loading..."
          : hasSearchText
            ? "No records Found"
            : ""
      }
      renderOption={(props, option: any) => {
        const fullName =
          `${option.firstName || ""} ${option.lastName || ""}`.trim();
        const initials =
          `${option.firstName?.charAt(0) || ""}${option.lastName?.charAt(0) || ""}`.toUpperCase();
        const statusStyle = getStatusColor(option.patientStatus);

        return (
          <PatientOptionWithTooltip
            {...props}
            key={option.patientUuid}
            name={fullName}
            style={{ ...props.style, padding: "8px 12px" }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                gap: 1.5,
              }}
            >
              <Avatar
                src={option.avatar}
                sx={{
                  width: 30,
                  height: 30,
                  bgcolor: AVATAR_COLOR.bg,
                  color: AVATAR_COLOR.color,
                  fontSize: 14,
                  fontWeight: 600,
                  flexShrink: 0,
                }}
              >
                {initials}
              </Avatar>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    noWrap
                    data-patient-name
                    sx={{ fontSize: "13px" }}
                  >
                    {fullName}
                  </Typography>
                  {option.duplicate && (
                    <Chip
                      icon={
                        <WarningAmberRoundedIcon
                          sx={{
                            fontSize: "11px !important",
                            color: "#D97706 !important",
                          }}
                        />
                      }
                      label="Similar Name"
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "10px",
                        fontWeight: 600,
                        backgroundColor: "#FFFBEB",
                        color: "#D97706",
                        border: "1px solid #FDE68A",
                        flexShrink: 0,
                        "& .MuiChip-label": { px: 0.75 },
                        "& .MuiChip-icon": { ml: 0.5 },
                      }}
                    />
                  )}
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    columnGap: 1.5,
                    mt: 0.25,
                  }}
                >
                  {option.birthDate ? (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: "11px", whiteSpace: "nowrap" }}
                    >
                      {formatDateWithAge(option.birthDate)}
                    </Typography>
                  ) : (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: "11px" }}
                    >
                      N/A
                    </Typography>
                  )}
                  {option.phone && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <PhoneIcon
                        sx={{ fontSize: 12, color: "text.secondary" }}
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: "11px" }}
                      >
                        {formatPhoneNumber(option.phone)}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              {option.patientStatus && (
                <Chip
                  label={
                    option.patientStatus.charAt(0) +
                    option.patientStatus.slice(1).toLowerCase()
                  }
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: "11px",
                    fontWeight: 600,
                    backgroundColor: statusStyle.bg,
                    color: statusStyle.color,
                    flexShrink: 0,
                  }}
                />
              )}
            </Box>
          </PatientOptionWithTooltip>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Name, DOB, or Phone"
          sx={{
            width: {
              xs: "150px",
              sm: "160px",
              md: "170px",
              lg: "210px",
              xl: "280px",
            },
            "& .MuiOutlinedInput-root": {
              height: 36,
              backgroundColor: "rgba(255,255,255,0.1)",
              borderRadius: "8px",
              color: "#FFF",
              paddingRight: "4px !important",
              "& fieldset": {
                borderColor: "rgba(255,255,255,0.2)",
              },
              "&:hover fieldset": {
                borderColor: "rgba(255,255,255,0.4)",
              },
              "&.Mui-focused fieldset": {
                borderColor: "rgba(255,255,255,0.6)",
              },
            },
            "& .MuiInputBase-input::placeholder": {
              color: "rgba(255,255,255,0.6)",
              opacity: 1,
            },
          }}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon
                  sx={{ color: "rgba(255,255,255,0.6)", fontSize: 20 }}
                />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end" sx={{ gap: 0.5 }}>
                {isFetching && (
                  <CircularProgress
                    size={16}
                    sx={{ color: "rgba(255,255,255,0.6)" }}
                  />
                )}
                <Box
                  sx={{
                    width: "1px",
                    height: 20,
                    backgroundColor: "rgba(255,255,255,0.3)",
                  }}
                />
                <Tooltip title="Add Patient">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(
                        `${window.location.origin}/ehr/patients/sign-up`,
                        "_blank",
                        "noopener,noreferrer",
                      );
                    }}
                    sx={{
                      color: "rgba(255,255,255,0.7)",
                      gap: 0.5,
                      "&:hover": {
                        color: "#FFF",
                        backgroundColor: "rgba(255,255,255,0.15)",
                      },
                    }}
                  >
                    <PersonAddAlt1Icon sx={{ fontSize: 18 }} />
                    <Typography
                      component="span"
                      sx={{
                        fontSize: "13px",
                        fontWeight: 500,
                        lineHeight: 1,
                      }}
                    >
                      Add
                    </Typography>
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />
      )}
    />
  );
};

export default PatientSearch;
